import { IPlayerAdapterAdItem } from 'library/ts/player/adapter/interfaces/ads/IPlayerAdapterAdItem';
import { IPlayerAdapterAdBreakMetadata } from 'library/ts/player/adapter/interfaces/events/detail/IPlayerAdapterAdBreakMetadata';
import { filterHttpUrls, isHttpUrl } from 'library/ts/utilities/modules/URL';
import { IPlutoAdClickData } from 'tag/ts/tag/ads/pluto/interfaces/IPlutoAdClickData';
import { IPlutoStitcherAdBreak } from 'tag/ts/tag/ads/pluto/interfaces/IPlutoStitcherAdBreak';
import { IPlutoStitcherSession } from 'tag/ts/tag/ads/pluto/interfaces/IPlutoStitcherSession';

/**
 * @copyright © Paramount Streaming
 * @author Daniel Silva
 * @description Resolves Pluto Stitcher ad clickthrough data from session.json
 */
export class PlutoAdClickResolver {

	#adBreakInfo: IPlayerAdapterAdBreakMetadata[ 'adBreakInfo' ];

	cacheAdBreakInfo = ( detail: IPlayerAdapterAdBreakMetadata ): void => {
		this.#adBreakInfo = detail?.adBreakInfo;
	};

	resolve = ( session: unknown, adInfo: IPlayerAdapterAdItem ): IPlutoAdClickData | undefined => {
		const adBreaks = ( session as IPlutoStitcherSession )?.adBreaks;

		if ( !Array.isArray( adBreaks ) || adBreaks.length === 0 ) {
			return undefined;
		}

		if ( !Number.isFinite( adInfo?.adPosition ) || adInfo?.adPosition < 1 ) {
			return undefined;
		}

		const adBreak = this.#resolveAdBreak( adBreaks );
		const ad = adBreak?.ads?.[ adInfo.adPosition - 1 ];
		const clickThrough = ad?.videoClicks?.clickThrough;

		if ( isHttpUrl( clickThrough ) === false ) {
			return undefined;
		}

		return {
			clickThrough,
			clickTrackingUrls: filterHttpUrls( ad?.videoClicks?.clickTracking )
		};
	};

	#resolveAdBreak = ( adBreaks: IPlutoStitcherAdBreak[] ): IPlutoStitcherAdBreak | undefined => {
		if ( adBreaks.length === 1 ) {
			const adBreak = adBreaks[ 0 ];

			if ( this.#adBreakInfo && this.#isExpectedAdBreak( adBreak ) === false ) {
				return undefined;
			}

			return adBreak;
		}

		return this.#resolveAdBreakByBreakId( adBreaks ) ?? this.#resolveAdBreakByPosition( adBreaks );
	};

	#resolveAdBreakByBreakId = ( adBreaks: IPlutoStitcherAdBreak[] ): IPlutoStitcherAdBreak | undefined => {
		const breakId = Number( this.#adBreakInfo?.breakId );

		if ( Number.isNaN( breakId ) ) {
			return undefined;
		}

		return adBreaks.find(
			( adBreak ) => {
				const parsedStartTime = Date.parse( adBreak?.startTime );
				return parsedStartTime === breakId && this.#isExpectedAdBreak( adBreak );
			}
		);
	};

	#resolveAdBreakByPosition = ( adBreaks: IPlutoStitcherAdBreak[] ): IPlutoStitcherAdBreak | undefined => {
		const breakIndex = this.#adBreakInfo?.adBreakPosition - 1;
		const adBreak = adBreaks[ breakIndex ];

		if ( !Number.isFinite( breakIndex ) || !adBreak ) {
			return undefined;
		}

		return this.#isExpectedAdBreak( adBreak ) ? adBreak : undefined;
	};

	#isExpectedAdBreak = ( adBreak: IPlutoStitcherAdBreak ): boolean => {
		const adTotal = this.#adBreakInfo?.adTotal;
		const adBreakDuration = this.#adBreakInfo?.adBreakDuration;
		const hasAdTotal = Number.isFinite( adTotal );
		const hasAdBreakDuration = Number.isFinite( adBreakDuration );

		if ( !hasAdTotal && !hasAdBreakDuration ) {
			return false;
		}

		const matchesAdTotal = !hasAdTotal || adBreak?.ads?.length === adTotal;
		const matchesDuration = !hasAdBreakDuration || Math.round( adBreakDuration * 1000 ) === adBreak?.adBreakDuration;

		return matchesAdTotal && matchesDuration;
	};
}